"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import ReactMarkdown from "react-markdown";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getTextToolCopy } from "@/lib/text-tool-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function MarkdownEditor() {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getTextToolCopy(locale);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "markdown-editor") ?? tools[0],
    locale
  );
  const [markdown, setMarkdown] = useState(copy.markdownSample);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(markdown);
  };

  return (

    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
             <Icon icon="mdi:language-markdown" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 rounded-[10px] bg-positivus-dark px-4 py-2 text-sm font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_4px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
        >
          <Icon icon={uiIcons.copy} className="h-4 w-4" />
          {copy.copyMarkdown}
        </button>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <label className="mb-3 block text-lg font-medium text-positivus-dark">
            {copy.editor}
          </label>
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            className="min-h-[600px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 font-mono text-sm text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
          />
        </div>

        <div>
           <label className="mb-3 block text-lg font-medium text-positivus-dark">
            {copy.livePreview}
          </label>
          <div className="min-h-[600px] rounded-[20px] border-2 border-positivus-dark bg-white p-6 overflow-y-auto prose prose-sm dark:prose-invert max-w-none prose-headings:text-positivus-dark prose-p:text-gray-600 prose-a:text-positivus-green prose-strong:text-positivus-dark prose-li:text-gray-600">
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
